/* eslint-disable */
import axios from 'axios'
import { useAuthStore } from '../store/auth'
import { useLoaderStore } from '../store/loader'
import { useToast } from 'vue-toastification'
import { local, STORAGE_KEYS } from '@/utils/storage'

const toast = useToast()
let lastErrorMessage = ''
let lastErrorAt = 0
let sessionExpiredToastShown = false
const IS_PROD = process.env.NODE_ENV === 'production'
const SUCCESS_TOAST_SKIP_PATHS = ['/special-mode/select', '/point-code', '/special-mode/leaderboard', '/logout', '/token/refresh']
const ERROR_TOAST_SKIP_PATHS = ['/special-mode/select', '/point-code', '/special-mode/leaderboard', '/logout', '/token/refresh']
export const log = (...args) => {
  if (!IS_PROD) {
    console.log(...args)
  }
}
export const warn = (...args) => {
  if (!IS_PROD) {
    console.warn(...args)
  }
}
export const errorLog = (...args) => {
  if (!IS_PROD) {
    console.error(...args)
  }
}

const LOCAL_STORAGE_OVERRIDE_KEY = 'coridor:api-base-url'
const DEBUG_BRAND_CODE_KEY = 'coridor:debug-brand-code'

function sanitizeBaseUrl (value) {
  if (!value || typeof value !== 'string') {
    return ''
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return ''
  }
  return trimmed.replace(/\/+$/, '')
}

function withApiPath (baseUrl) {
  if (!baseUrl) {
    return '/api'
  }

  return /\/api$/i.test(baseUrl) ? baseUrl : `${baseUrl}/api`
}

function normalizeApiPath (path) {
  const rawPath = String(path || '').trim()
  if (!rawPath) {
    return ''
  }

  const withLeadingSlash = rawPath.startsWith('/') ? rawPath : `/${rawPath}`

  if (withLeadingSlash.startsWith('/api/')) {
    return withLeadingSlash.slice(4)
  }

  return withLeadingSlash
}

function getBrowserOverride () {
  return local.get(LOCAL_STORAGE_OVERRIDE_KEY) || ''
}

function isLocalhostRuntime () {
  if (typeof window === 'undefined') {
    return false
  }

  const host = window.location.hostname
  return host === 'localhost' || host === '127.0.0.1'
}

function isSameOriginDevBase (baseUrl) {
  return baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')
}

function getDebugBrandCode () {
  if (typeof window === 'undefined') {
    return ''
  }

  const params = new URLSearchParams(window.location.search)
  const queryBrandCode = (params.get('brandCode') || '').trim().toLowerCase()

  if (queryBrandCode) {
    local.set(DEBUG_BRAND_CODE_KEY, queryBrandCode)
    return queryBrandCode
  }

  return (local.get(DEBUG_BRAND_CODE_KEY) || '').trim().toLowerCase()
}

function resolveBaseUrl () {
  const envLocalBase = sanitizeBaseUrl(process.env.VUE_APP_API_BASE_URL_LOCAL)
  if (envLocalBase) {
    return withApiPath(envLocalBase)
  }

  if (!IS_PROD && isLocalhostRuntime()) {
    const browserOverride = sanitizeBaseUrl(getBrowserOverride())
    if (browserOverride) {
      if (isSameOriginDevBase(browserOverride)) {
        return withApiPath(browserOverride)
      }

      // Prevent local dev from hitting remote origins directly and triggering CORS.
      return '/api'
    }
  }

  const envBase = sanitizeBaseUrl(process.env.VUE_APP_API_BASE_URL)
  if (envBase) {
    if (!IS_PROD && isLocalhostRuntime() && !isSameOriginDevBase(envBase)) {
      return '/api'
    }

    return withApiPath(envBase)
  }

  if (!IS_PROD) {
    const browserOverride = sanitizeBaseUrl(getBrowserOverride())
    if (browserOverride && isSameOriginDevBase(browserOverride)) {
      return withApiPath(browserOverride)
    }
  }

  return '/api'
}

const BASE_URL = resolveBaseUrl()

function shouldSkipToast (requestPath, skipPaths) {
  return skipPaths.some(path => requestPath.includes(path))
}

function notifySessionExpiredOnce () {
  if (sessionExpiredToastShown) {
    return
  }

  const hasStoredSession = local.get(STORAGE_KEYS.IS_LOGIN) === 'true'
  if (!hasStoredSession) {
    return
  }

  sessionExpiredToastShown = true
  toast.error('Oturumunuz sonlandi. Lutfen tekrar giris yapin.')

  window.setTimeout(() => {
    sessionExpiredToastShown = false
  }, 2500)
}

axios.defaults.baseURL = BASE_URL
axios.defaults.withCredentials = true

function shouldSkipGlobalLoader (config) {
  return !!(
    config?.skipGlobalLoader ||
    config?.headers?.['X-Skip-Global-Loader'] ||
    isGlobalLoaderSuppressedRoute()
  )
}

function isGlobalLoaderSuppressedRoute () {
  if (typeof window === 'undefined') {
    return false
  }

  const path = window.location.pathname || ''
  return path.includes('/home-map')
}

  axios.interceptors.request.use(
    function (config) {
      try {
        if (!shouldSkipGlobalLoader(config)) {
          const loaderStore = useLoaderStore()
          loaderStore.startRequest()
        }
        const locale = local.get(STORAGE_KEYS.LOCALE) || 'tr'
        config.headers = config.headers || {}
        // Note: Browsers block programmatic setting of 'Accept-Language'.
        // Use a custom header that backend reads.
        config.headers['X-Locale'] = locale
        // Keep for completeness (won't override in browser)
        config.headers['Accept-Language'] = locale
        const debugBrandCode = !IS_PROD ? getDebugBrandCode() : ''
        if (debugBrandCode) {
          config.headers['X-Debug-Brand-Code'] = debugBrandCode
        }
      } catch (_) {}
      return config
    },
    function (error) {
      if (!shouldSkipGlobalLoader(error?.config)) {
        const loaderStore = useLoaderStore()
        loaderStore.endRequest()
      }
      return Promise.reject(error)
    }
  )
  
  axios.interceptors.response.use(
    function (response) {
      if (!shouldSkipGlobalLoader(response?.config)) {
        const loaderStore = useLoaderStore()
        loaderStore.endRequest()
      }
      return response
    },
    async function (error) {
      if (!shouldSkipGlobalLoader(error?.config)) {
        const loaderStore = useLoaderStore()
        loaderStore.endRequest()
      }
      const authStore = useAuthStore()
      const originalRequest = error.config
      const response = error.response
      if (!response) {
        return Promise.reject(error)
      }
      if (response.status === 401 && response.data.message === 'Expired JWT Token') {
        log(response.data.message)
      }

      log(response.status, originalRequest.url)
      if (
        (response.status === 401 || response.status === 404 || response.status >= 500) &&
        originalRequest.url.includes('token/refresh')
      ) {
        if (response.status >= 500 && !originalRequest._refreshRetry) {
          // Transient server error — retry the refresh once before giving up
          originalRequest._refreshRetry = true
          try {
            await new Promise(resolve => setTimeout(resolve, 1000))
            return axios(originalRequest)
          } catch (_) {}
        }
        log('refresh token didnt work')
        notifySessionExpiredOnce()
        await authStore.logout({ skipApi: true })
        return Promise.reject(error)
      } else if (response.status === 401 && !originalRequest._retry) {
        log('retry!')
        originalRequest._retry = true
        try {
          await authStore.renewToken()
          const freshToken = authStore.getToken()
          if (freshToken) {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers.Authorization = `Bearer ${freshToken}`
          }
          return axios(originalRequest)
        } catch (renewError) {
          notifySessionExpiredOnce()
          await authStore.logout({ skipApi: true })
          return Promise.reject(renewError)
        }
      }
      return Promise.reject(error)
    }
  )

export class BaseService {
getFullUrl (path) {
  // axios.defaults.baseURL already contains the resolved API base.
  return normalizeApiPath(path)
}

getHeaders (payload = null) {
    const authStore = useAuthStore()
        const headers = {}
        const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData

        if (!isFormData) {
          headers['Content-Type'] = 'application/json'
        }

        if (authStore.getToken && authStore.getToken()) {
          return { headers: { ...headers, Authorization: `Bearer ${authStore.getToken()}` } }
        }
        return { headers }
    }
handleSuccess (response) {
    const resultMessage = (response && response.data && response.data.message) || response.message || response.toString()
    if (resultMessage === '[object Object]') return

    const requestPath = response?.config?.url || ''
    const shouldSkipAutoToast = Boolean(response?.config?.skipSuccessToast) || shouldSkipToast(requestPath, SUCCESS_TOAST_SKIP_PATHS)

    if (!shouldSkipAutoToast) {
      toast.success(resultMessage)
    }
  }
handleError (error) {
  errorLog(error)
    let errorMessage = 'Undefined error!'
    if (undefined !== error.response) {
      errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
    }

    if (errorMessage === 'Missing JWT Refresh Token') errorMessage = 'Lütfen kullanıcı bilgilerinizi kontrol ediniz.'
    if (errorMessage === 'Invalid credentials.') errorMessage = 'Kullanıcı bilgileriniz hatalı.'

    const requestPath = error?.config?.url || ''
    const shouldSkipAutoToast = Boolean(error?.config?.skipErrorToast) || shouldSkipToast(requestPath, ERROR_TOAST_SKIP_PATHS)
    const now = Date.now()
    const isDuplicateError = errorMessage === lastErrorMessage && now - lastErrorAt < 2500

    if (
      !shouldSkipAutoToast &&
      !isDuplicateError &&
        errorMessage !== 'Internal Server Error' &&
        errorMessage !== 'Undefined error!'
    ) {
      toast.error(errorMessage)
      lastErrorMessage = errorMessage
      lastErrorAt = now
    } else {
      log('Service error:', errorMessage)
    }
    return error
  }

post (path, formData, requestConfig = {}) {
  log('//POST--->')
  log(path)
  log(formData)
  log('//<--POST')
    return axios
      .post(this.getFullUrl(path), formData, { ...this.getHeaders(formData), ...requestConfig }).then(
        response => {
          this.handleSuccess(response)
          return response
        },
        error => {
          this.handleError(error)
          return Promise.reject(error)
        }
      )
  }
  put (path, formData) {
    return axios
      .put(this.getFullUrl(path), formData, this.getHeaders(formData)).then(
        response => {
          this.handleSuccess(response)
          log(response)
          return response
        },
        error => {
          this.handleError(error)
          return Promise.reject(error)
        }
      )
  }
  get (path, requestConfig = {}) {
    return axios
        .get(this.getFullUrl(path), { ...this.getHeaders(), ...requestConfig }).then(
        response => {
          this.handleSuccess(response)
          log(response)
          return response
        },
        error => {
          this.handleError(error)
          return Promise.reject(error)
        }
      )
  }

  delete (path, data = {}) {
    return axios
      .delete(this.getFullUrl(path), { ...this.getHeaders(), data }).then(
        response => {
          this.handleSuccess(response)
          log(response)
          return response
        },
        error => {
          this.handleError(error)
          return Promise.reject(error)
        }
      )
  }


}
 /* eslint-disable */ 
